'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

import {
  ClaimType,
  createClaimNotLoggedIn,
  fetchOrderInfo,
  fetchReturnReasons,
} from '@eliadmin/core/src/api/claims/claims-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { fetchShipments } from '@eliadmin/core/src/api/general/general-api';
import {
  CaptchaAction,
  useRecaptcha,
} from '@eliadmin/core/src/components/providers/captcha-provider';
import {
  ChoiceCards,
  SelectField,
  TextareaField,
  TextField,
} from '@eliadmin/core/src/components/svetech/forms/fields';
import {
  FormAlert,
  FormSection,
  fieldGridClassName,
} from '@eliadmin/core/src/components/svetech/forms/form-parts';
import { FormSuccess } from '@eliadmin/core/src/components/svetech/forms/form-success';
import { ButtonLink } from '@eliadmin/core/src/components/svetech/ui/button';
import { CmsImage } from '@eliadmin/core/src/components/svetech/ui/cms-image';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useDebounce } from '@eliadmin/core/src/hooks/use-debounce';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { formatPrice, roundPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type ClaimFormProps = {
  className?: {
    root?: string;
    header?: string;
    field?: string;
    grid?: string;
    section_header?: string;
  };
};

const claimSchema = (VM: FormValidationMessages) =>
  z
    .object({
      order_id: z.string(),
      shipment_id: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      street_number: z.string(),
      street: z.string(),
      city: z.string(),
      swift_code: z.string().optional(),
      iban: z.string().nullable().optional(),
      post_code: z.string(),
      first_name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      last_name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      return_reason: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      note: z.string(),
      type: z
        .enum([ClaimType.EXCHANGE, ClaimType.RETURN, ClaimType.REPAIR])
        .default(ClaimType.RETURN),
      // TODO: validate items
      items: z
        .array(z.object({ id: z.number(), quantity: z.number() }))
        .min(1, VM.AT_LEAST_ONE_PRODUCT_VALIDATION_MESSAGE()),
    })
    .superRefine((data, ctx) => {
      if (data.type === ClaimType.RETURN && (!data.iban || data.iban.trim().length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
          path: ['iban'],
        });
      }
    });
type ClaimFormInputValues = z.input<ReturnType<typeof claimSchema>>;
type ClaimFormValues = z.output<ReturnType<typeof claimSchema>>;

/**
 * Online return / exchange / repair (core logic unchanged): the order number and e-mail look
 * the order up, then the customer data, the goods and the reason appear. Svetech layout: the
 * claim type as radio cards, H4 sections, the order lines as selectable cards and the
 * confirmation panel of the contact block after sending.
 */
export function ClaimForm({ className }: ClaimFormProps) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const schema = claimSchema(formValidationMessages);

  const form = useForm<ClaimFormInputValues, any, ClaimFormValues>({
    defaultValues: {
      order_id: '',
      shipment_id: '',
      street_number: '',
      street: '',
      city: '',
      swift_code: '',
      iban: '',
      post_code: '',
      first_name: '',
      last_name: '',
      email: '',
      return_reason: '',
      note: '',
      type: ClaimType.RETURN,
      items: [],
    },
    resolver: zodResolver(schema),
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const { executeRecaptcha } = useRecaptcha();

  const { data: shipments = { data: [] }, isLoading: isShipmentsLoading } = useQuery({
    queryKey: [CacheKey.SHIPMENTS, locale],
    queryFn: () => fetchShipments({ locale }),
  });

  const { data: returnReasons } = useQuery({
    queryKey: [CacheKey.RETURN_REASONS, locale],
    queryFn: () => fetchReturnReasons({ locale }),
  });

  const [searchQueryOrderInfo, setsSearchQueryOrderInfo] = useState({ email: '', identifier: '' });

  const debouncedOrderInfo = useDebounce(searchQueryOrderInfo, 500);

  const { data: cartItems, isLoading } = useQuery({
    queryKey: [CacheKey.ORDER_INFO, locale, debouncedOrderInfo],
    queryFn: () =>
      fetchOrderInfo({
        locale,
        identifier: debouncedOrderInfo.identifier,
        email: debouncedOrderInfo.email,
      }).catch(() => null),
    enabled: debouncedOrderInfo.email.length > 1 && debouncedOrderInfo.identifier.length > 1,
  });

  useEffect(() => {
    if (!cartItems?.data) return;

    form.setValue('first_name', cartItems.data.name);
    form.setValue('last_name', cartItems.data.surname);
    form.setValue('street_number', cartItems.data.street_number);
    form.setValue('city', cartItems.data.city);
    form.setValue('post_code', cartItems.data.postcode);
    form.setValue('street', cartItems.data.address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems?.data]);

  useEffect(() => {
    if (returnReasons?.data?.length && !form.getValues('return_reason')) {
      form.setValue('return_reason', String(returnReasons.data[0].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnReasons?.data]);

  useEffect(() => {
    if (cartItems?.data?.items?.length === 1 && form.getValues('items').length === 0) {
      const singleItem = cartItems.data.items[0];
      form.setValue('items', [{ id: singleItem.id, quantity: 1 }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems?.data?.items]);

  const type = useWatch({ control: form.control, name: 'type' });
  const [sent, setSent] = useState(false);
  /** Form-level message: the core only marked fields, other failures went unnoticed. */
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Returned promise → `formState.isSubmitting` covers the captcha and the request.
  const handleSubmit = form.handleSubmit(async values => {
    setSubmitError(null);
    try {
      const captchaResponse = executeRecaptcha
        ? await executeRecaptcha(CaptchaAction.CLAIM)
        : undefined;

      await createClaimNotLoggedIn({
        locale,
        body: {
          type: values.type,
          identifier: values.order_id,
          reason: { id: parseInt(values.return_reason) },
          shipment: { id: parseInt(values.shipment_id) },
          order: { id: parseInt(values.order_id) },
          name: values.first_name,
          surname: values.last_name,
          email: values.email,
          address: values.street,
          street_number: values.street_number,
          city: values.city,
          postcode: values.post_code,
          note: values.note,
          description: values.note,
          iban: values.iban ?? '',
          swift: values.swift_code ?? '',
          items: values.items,
          'g-recaptcha-response': captchaResponse,
        },
      });
      setSent(true);
    } catch (error) {
      if (error instanceof ValidationError) {
        for (const [field, errors] of Object.entries(error.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        setSubmitError(t('text4008'));
        return;
      }
      setSubmitError(t('text320'));
    }
  });

  if (sent) {
    return (
      <FormSuccess title={t('text713')}>
        <ButtonLink href="/" className="max-md:w-full">
          {t('text725')}
        </ButtonLink>
      </FormSuccess>
    );
  }

  const order = cartItems?.data;
  const fieldClassName = cn('ea-claim-form__field', className?.field);

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={handleSubmit}
        className={cn('flex flex-col gap-8 md:gap-10', 'ea-claim-form', className?.root)}
      >
        <FormField
          name="type"
          control={form.control}
          render={({ field }) => (
            <FormItem className={cn('ea-claim-form__header', className?.header)}>
              <ChoiceCards
                id="claim-type"
                label={t('text372')}
                value={field.value ?? ClaimType.RETURN}
                onChange={field.onChange}
                options={[
                  { value: ClaimType.RETURN, label: t('text3841') },
                  { value: ClaimType.EXCHANGE, label: t('text289') },
                  { value: ClaimType.REPAIR, label: t('text3842') },
                ]}
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <FormSection
          title={t('text291')}
          className={cn('ea-claim-form__section-header', className?.section_header)}
        >
          <div
            className={cn(fieldGridClassName, 'ea-claim-form__personal-data-grid', className?.grid)}
          >
            <TextField
              control={form.control}
              name="order_id"
              label={t('text108')}
              placeholder={t('text108')}
              required
              onChange={event => {
                setsSearchQueryOrderInfo({
                  email: searchQueryOrderInfo.email,
                  identifier: event.target.value,
                });
                form.setValue('order_id', event.target.value);
              }}
              className={cn('ea-claim-form__product-id-field', fieldClassName)}
            />
            <TextField
              control={form.control}
              name="email"
              type="email"
              autoComplete="email"
              label={t('text35')}
              placeholder={t('text259')}
              required
              onChange={event => {
                setsSearchQueryOrderInfo({
                  identifier: searchQueryOrderInfo.identifier,
                  email: event.target.value,
                });
                form.setValue('email', event.target.value);
              }}
              className={cn('ea-claim-form__email-field', fieldClassName)}
            />

            {order ? (
              <>
                <TextField
                  control={form.control}
                  name="first_name"
                  label={t('text33')}
                  required
                  disabled
                  className={cn('ea-claim-form__first-name-field', fieldClassName)}
                />
                <TextField
                  control={form.control}
                  name="last_name"
                  label={t('text34')}
                  required
                  disabled
                  className={cn('ea-claim-form__last-name-field', fieldClassName)}
                />
                <TextField
                  control={form.control}
                  name="street"
                  label={t('text38')}
                  required
                  disabled
                  className={cn('ea-claim-form__street-field', fieldClassName)}
                />
                <TextField
                  control={form.control}
                  name="street_number"
                  label={t('text438')}
                  required
                  disabled
                  className={cn('ea-claim-form__street-number-field', fieldClassName)}
                />
                <TextField
                  control={form.control}
                  name="city"
                  label={t('text39')}
                  required
                  disabled
                  className={cn('ea-claim-form__city-field', fieldClassName)}
                />
                <TextField
                  control={form.control}
                  name="post_code"
                  label={t('text40')}
                  required
                  disabled
                  className={cn('ea-claim-form__post-code-field', fieldClassName)}
                />
              </>
            ) : null}
          </div>

          {/* Order lookup state; `aria-live` announces the result of typing. */}
          <div aria-live="polite" aria-busy={isLoading}>
            {isLoading ? (
              <div className="flex flex-col gap-3" aria-label={t('text439')}>
                <Skeleton className="h-14 w-full rounded-xl rounded-tr-none md:h-[50px] md:rounded-[15px] md:rounded-tr-none" />
                <Skeleton className="h-14 w-2/3 rounded-xl rounded-tr-none md:h-[50px] md:rounded-[15px] md:rounded-tr-none" />
              </div>
            ) : !order?.items.length ? (
              <FormAlert tone="info" live="none">
                {t('text726')}
              </FormAlert>
            ) : null}
          </div>
        </FormSection>

        {order ? (
          <>
            <FormSection title={t('text150')} description={t('text297')}>
              <FormField
                name="items"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-3">
                    {order.items.map(orderItem => {
                      const isSelected = !!field.value?.find(item => item.id === orderItem.id);
                      const inputId = `claim-item-${orderItem.id}`;

                      return (
                        <label
                          key={orderItem.id}
                          htmlFor={inputId}
                          className={cn(
                            'flex cursor-pointer items-center gap-3 rounded-xl rounded-tr-none border border-[#e6e8e2] bg-white p-3 transition-colors hover:border-[#c9cdc4] motion-reduce:transition-none md:gap-4 md:rounded-[15px] md:rounded-tr-none md:p-4',
                            isSelected && 'border-[#2c7a25] bg-[#eff6ed] hover:border-[#2c7a25]',
                          )}
                        >
                          <FormControl>
                            <Checkbox
                              id={inputId}
                              value={orderItem.id}
                              checked={isSelected}
                              onCheckedChange={checked => {
                                field.onChange(
                                  checked
                                    ? [...field.value, { id: orderItem.id, quantity: 1 }]
                                    : field.value.filter(item => item.id !== orderItem.id),
                                );
                              }}
                            />
                          </FormControl>
                          <span className="relative size-14 shrink-0 overflow-hidden rounded-lg rounded-tr-none bg-[#f2f3ef] md:size-16">
                            <CmsImage
                              image={
                                orderItem.image
                                  ? { ...orderItem.image, description: orderItem.image.description ?? undefined }
                                  : null
                              }
                              decorative
                              sizes="64px"
                            />
                          </span>
                          <span className="flex min-w-0 flex-1 flex-col gap-1 md:flex-row md:items-center md:gap-4">
                            <span className="svt-body min-w-0 flex-1 font-semibold text-[#14171a]">
                              {orderItem.name}
                            </span>
                            <span className="svt-body shrink-0 text-[#4a5560]">
                              {formatPrice(
                                roundPrice(orderItem.price_with_vat || orderItem.price),
                                currency,
                                currencySettings,
                              )}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection title={t('text111')}>
              <div className={fieldGridClassName}>
                {type === ClaimType.RETURN ? (
                  <TextField
                    control={form.control}
                    name="iban"
                    label={t('text294')}
                    placeholder={t('text294')}
                    required
                    autoComplete="off"
                    className={cn('ea-claim-form__iban-field md:col-span-2', fieldClassName)}
                  />
                ) : null}
                {returnReasons ? (
                  <SelectField
                    control={form.control}
                    name="return_reason"
                    label={t('text296')}
                    placeholder={t('text296')}
                    required
                    options={returnReasons.data.map(reason => ({
                      label: t(reason.name as any),
                      value: reason.id,
                    }))}
                    className={cn('ea-claim-form__reason_id-field', fieldClassName)}
                  />
                ) : null}
                {isShipmentsLoading ? (
                  <Skeleton className="h-14 w-full self-end rounded-xl rounded-tr-none md:h-[50px] md:rounded-[15px] md:rounded-tr-none" />
                ) : (
                  <SelectField
                    control={form.control}
                    name="shipment_id"
                    label={t('text153')}
                    placeholder={t('text153')}
                    required
                    options={shipments.data.map(shipment => ({
                      label: t(shipment.name as any),
                      value: shipment.id,
                    }))}
                    className={cn('ea-claim-form__shipment-id-field', fieldClassName)}
                  />
                )}
                <TextareaField
                  control={form.control}
                  name="note"
                  label={t('text57')}
                  className={cn('ea-claim-form__note-field md:col-span-2', fieldClassName)}
                />
              </div>
            </FormSection>

            <div className="flex flex-col gap-4">
              {submitError ? <FormAlert tone="error">{submitError}</FormAlert> : null}
              <div className="ea-claim-form__submit-button-container flex md:justify-end">
                <Button
                  type="submit"
                  isLoading={form.formState.isSubmitting}
                  className="ea-claim-form__submit-button w-full md:w-auto"
                >
                  {type === ClaimType.RETURN ? t('text3865') : t('text185')}
                </Button>
              </div>
            </div>
          </>
        ) : null}
      </form>
    </Form>
  );
}
